import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    X,
    ChevronRight,
    ChevronLeft,
    Sparkles,
    Clock,
    ListTodo,
    Calendar,
    QrCode,
    Settings,
    Moon,
    Play,
    Pause,
    CheckCircle,
    Archive,
    Bell,
    BookOpen,
} from "lucide-react";

interface TutorialProps {
    onComplete: () => void;
    onSkip: () => void;
}

interface TutorialStep {
    title: string;
    description: string;
    icon: React.ReactNode;
    tip?: string;
}

const tutorialSteps: TutorialStep[] = [
    {
        title: "Welcome to TaskMaster! 🎉",
        description:
            "TaskMaster helps you stay focused and track your time effectively. Let's walk through the key features to get you started.",
        icon: <Sparkles className="w-8 h-8 text-yellow-500" />,
        tip: "You can skip this tutorial anytime and come back later from Settings.",
    },
    {
        title: "Adding Tasks",
        description:
            "Add tasks with an estimated time. This helps you plan your day realistically and stay on track. You can add tasks to 'Today' for immediate work or to 'Backlog' for later.",
        icon: <ListTodo className="w-8 h-8 text-violet-500" />,
        tip: "Be realistic with your time estimates - it's okay to adjust them later!",
    },
    {
        title: "Starting & Managing Tasks",
        description:
            "Click the play button to start a task. Only one task can be active at a time to help you focus. You can pause, add more time, or complete tasks as you work.",
        icon: <Play className="w-8 h-8 text-green-500" />,
        tip: "The timer continues even if you close the browser!",
    },
    {
        title: "Time Tracking",
        description:
            "Watch your time count down as you work. When you go overtime, you'll hear an alarm and see a dialog with options: continue working, add more time, or mark as complete.",
        icon: <Clock className="w-8 h-8 text-blue-500" />,
        tip: "Adjust the estimated time anytime by clicking +5/-5 minutes on a task.",
    },
    {
        title: "Completing Tasks",
        description:
            "When you finish a task, mark it complete! You'll see a celebration with confetti. Completed tasks can be 'undone' if you need to work on them more.",
        icon: <CheckCircle className="w-8 h-8 text-green-500" />,
        tip: "Celebrate your wins - every completed task is progress!",
    },
    {
        title: "Backlog",
        description:
            "Use the Backlog to store tasks you're not working on today. Move tasks between Today and Backlog as your priorities change.",
        icon: <Archive className="w-8 h-8 text-amber-500" />,
        tip: "Great for brainstorming tasks without cluttering your daily list.",
    },
    {
        title: "Calendar View",
        description:
            "Switch to the Calendar tab to see your productivity history. View stats by day, week, month, or year. Click any day to see what you accomplished.",
        icon: <Calendar className="w-8 h-8 text-blue-500" />,
        tip: "Track your streak to stay motivated!",
    },
    {
        title: "Notifications & Wake Lock",
        description:
            "Enable browser notifications in Settings to get alerts when tasks go overtime or complete. The Wake Lock keeps your screen on while working.",
        icon: <Bell className="w-8 h-8 text-purple-500" />,
        tip: "Enable notifications to never miss a timer!",
    },
    {
        title: "Share via QR Code",
        description:
            "Click the QR code icon to share TaskMaster with others. They can scan it to open the app on their device.",
        icon: <QrCode className="w-8 h-8 text-violet-500" />,
        tip: "Share productivity with friends and colleagues!",
    },
    {
        title: "Dark Mode & Settings",
        description:
            "Toggle dark mode with the moon/sun icon. Access Settings for notifications, wake lock, and to replay this tutorial anytime.",
        icon: <Moon className="w-8 h-8 text-slate-500" />,
        tip: "Dark mode is easier on the eyes during late work sessions.",
    },
    {
        title: "You're Ready! 🚀",
        description:
            "That's everything you need to know! A 'Go through tutorial' task has been created for you to practice with. Start it to experience the timer in action!",
        icon: <Sparkles className="w-8 h-8 text-yellow-500" />,
        tip: "Focus on one task at a time and watch your productivity soar!",
    },
];

export function Tutorial({ onComplete, onSkip }: TutorialProps) {
    const [currentStep, setCurrentStep] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    const step = tutorialSteps[currentStep];
    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === tutorialSteps.length - 1;
    const progress = ((currentStep + 1) / tutorialSteps.length) * 100;

    const nextStep = useCallback(() => {
        if (isLastStep) {
            onComplete();
            return;
        }
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentStep((prev) =>
                Math.min(prev + 1, tutorialSteps.length - 1)
            );
            setIsAnimating(false);
        }, 150);
    }, [isLastStep, onComplete]);

    const prevStep = useCallback(() => {
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentStep((prev) => Math.max(prev - 1, 0));
            setIsAnimating(false);
        }, 150);
    }, []);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowRight" || e.key === "Enter") {
                nextStep();
            } else if (e.key === "ArrowLeft") {
                prevStep();
            } else if (e.key === "Escape") {
                onSkip();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [nextStep, prevStep, onSkip]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <Card className="w-full max-w-lg border-0 shadow-2xl overflow-hidden">
                {/* Progress bar */}
                <div className="h-1 bg-slate-200 dark:bg-slate-700">
                    <div
                        className="h-full bg-linear-to-r from-violet-500 to-purple-600 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                <CardHeader className="pb-2 relative">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <BookOpen className="w-4 h-4" />
                            Step {currentStep + 1} of {tutorialSteps.length}
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={onSkip}
                            title="Skip tutorial (Esc)"
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                </CardHeader>

                <CardContent
                    className={`pt-2 pb-6 transition-opacity duration-150 ${
                        isAnimating ? "opacity-0" : "opacity-100"
                    }`}
                >
                    {/* Icon */}
                    <div className="flex justify-center mb-6">
                        <div className="p-4 rounded-2xl bg-linear-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 shadow-inner">
                            {step.icon}
                        </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-xl font-bold text-center mb-3">
                        {step.title}
                    </h2>

                    {/* Description */}
                    <p className="text-center text-muted-foreground leading-relaxed mb-4">
                        {step.description}
                    </p>

                    {/* Tip */}
                    {step.tip && (
                        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-center">
                            <p className="text-sm text-amber-800 dark:text-amber-200">
                                💡 <span className="font-medium">Tip:</span>{" "}
                                {step.tip}
                            </p>
                        </div>
                    )}

                    {/* Navigation */}
                    <div className="flex items-center justify-between mt-6 gap-3">
                        <Button
                            variant="outline"
                            onClick={prevStep}
                            disabled={isFirstStep}
                            className="flex-1"
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Back
                        </Button>

                        <Button
                            onClick={nextStep}
                            className="flex-1 bg-linear-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
                        >
                            {isLastStep ? (
                                <>
                                    Get Started
                                    <Sparkles className="w-4 h-4 ml-1" />
                                </>
                            ) : (
                                <>
                                    Next
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </>
                            )}
                        </Button>
                    </div>

                    {/* Skip button */}
                    {!isLastStep && (
                        <button
                            onClick={onSkip}
                            className="w-full mt-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                            Skip tutorial
                        </button>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
